import { safeLocalStorage } from "@/utils/safeStorage";
import { useState, useEffect, useCallback } from 'react';

export interface GamificationSettings {
  streakNotifications: boolean;
  commandOrbVisible: boolean;
  moodAdaptiveTheme: boolean;
  highContrastMode: boolean;
  reduceMotion: boolean;
  soundEffectsVolume: number;
  achievementToasts: boolean;
  leaderboardVisible: boolean;
  // UI V2 GenieGuide settings
  genieGuideEnabled: boolean;
  genieGuideVoiceEnabled: boolean;
}

const STORAGE_KEY = 'geniebot-gamification-settings';

const DEFAULT_SETTINGS: GamificationSettings = {
  streakNotifications: true,
  commandOrbVisible: true,
  moodAdaptiveTheme: true,
  highContrastMode: false,
  reduceMotion: false,
  soundEffectsVolume: 0.7,
  achievementToasts: true,
  leaderboardVisible: true,
  // UI V2 GenieGuide defaults
  genieGuideEnabled: true,
  genieGuideVoiceEnabled: false,
};

export const useGamificationSettings = () => {
  const [settings, setSettings] = useState<GamificationSettings>(() => {
    if (typeof window === 'undefined') return DEFAULT_SETTINGS;
    try {
      const stored = safeLocalStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_SETTINGS, ...JSON.parse(stored) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  useEffect(() => {
    try {
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save gamification settings:', error);
    }
  }, [settings]);

  const updateSetting = useCallback(<K extends keyof GamificationSettings>(
    key: K,
    value: GamificationSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return {
    settings,
    updateSetting,
    resetToDefaults,
  };
};
